import { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import useModal from '../../../hooks/use-close-modal.hook';
import { RoutesService } from '../../../services/routes.service';
import type { PreviewResult, SalienteDto, WriteResult } from '../../../services/opensips.types';
import { AlertService } from '../../../services/alert.service';
import Button from '../../../components/forms/button.component';
import Input from '../../../components/forms/input.component';
import Icon from '../../../components/forms/icon.component';
import { OpensipsPreview, ReloadBanner } from './opensips-preview.component';

interface Props {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  /** SalienteDto crudo */
  saliente: SalienteDto;
}

/**
 * Ajuste rápido de capacidad de UN recurso de una salida (PUT /salientes/:id/capacity).
 * Capacidad 0 = desactivar ese recurso. Mismo flujo dryRun → aplicar.
 */
export default function CapacityModal({ show, onRequestClose, saliente }: Props) {
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);

  const opts = saliente.resources.map((r) => ({
    value: r.resource,
    label: `${r.resource} (actual ${r.capacity})`
  }));
  const [resource, setResource] = useState(opts[0]?.value ?? '');
  const [capacity, setCapacity] = useState(String(saliente.resources[0]?.capacity ?? 0));
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [result, setResult] = useState<WriteResult<SalienteDto> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const cur = saliente.resources.find((r) => r.resource === resource);
    if (cur) setCapacity(String(cur.capacity));
    setPreview(null);
  }, [resource, saliente]);

  const close = (refresh: boolean) => {
    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    onRequestClose(refresh);
  };

  const valid = resource !== '' && /^\d+$/.test(capacity);

  const doPreview = async () => {
    try {
      setBusy(true);
      setPreview(
        await RoutesService.previewCapacity(saliente.id, { resource, capacity: Number(capacity) })
      );
    } catch (error: any) {
      AlertService.toastError(error?.error || 'El backend rechazó el preview');
    } finally {
      setBusy(false);
    }
  };

  const doApply = async () => {
    try {
      setBusy(true);
      const res = await RoutesService.setCapacity(saliente.id, {
        resource,
        capacity: Number(capacity)
      });
      setResult(res);
      if (res.reload?.ok) AlertService.toastSuccess('Capacidad aplicada y lb_reload OK');
      else AlertService.toastError('Capacidad guardada pero lb_reload falló');
    } catch (error: any) {
      AlertService.toastError(error?.error || 'No se pudo aplicar la capacidad');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      ref={modalRef}
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      data-bs-backdrop="static"
    >
      <div className="modal-dialog modal-dialog-centered mw-650px">
        <div className="modal-content shadow-lg border-0">
          <div className="modal-header justify-content-end border-0 pb-0 pt-4 px-6">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-light-danger rounded-circle"
              onClick={() => close(!!result)}
            >
              <i className="ki-duotone ki-cross fs-2">
                <span className="path1" />
                <span className="path2" />
              </i>
            </button>
          </div>
          <div className="modal-body pt-0 pb-10 px-6 px-xl-10">
            <div className="text-center mb-6">
              <h1 className="mb-2 fs-2x">Ajustar capacidad</h1>
              <p className="text-muted fw-semibold fs-6">
                Salida #{saliente.id} · grupo {saliente.groupId} · <code>{saliente.dstUri}</code>
              </p>
              <div className="separator border-2 my-4"></div>
            </div>

            {!result && (
              <>
                <div className="row g-5">
                  <div className="col-md-7">
                    <label className="form-label required">Recurso</label>
                    <Select
                      options={opts}
                      className="form-select-custom"
                      classNamePrefix="form-select-custom"
                      value={opts.find((o) => o.value === resource) ?? null}
                      onChange={(o: any) => setResource(o?.value ?? '')}
                      isDisabled={!!preview}
                    />
                  </div>
                  <div className="col-md-5">
                    <Input
                      label="Nueva capacidad"
                      requiredIndicator="required"
                      type="number"
                      value={capacity}
                      disabled={!!preview}
                      onChange={(e: any) => setCapacity(e.target.value)}
                    />
                    <div className="form-text">0 = desactivar este recurso</div>
                  </div>
                </div>

                {preview && (
                  <div className="mt-5">
                    <OpensipsPreview
                      table="load_balancer"
                      action={preview.action}
                      record={preview.record}
                    />
                  </div>
                )}

                <div className="d-flex justify-content-center align-items-center pt-8 gap-3">
                  {!preview ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-light fs-7 fw-bold"
                        onClick={() => close(false)}
                      >
                        Cancelar
                      </button>
                      <Button
                        type="button"
                        className="btn btn-primary fs-7 fw-bold"
                        disabled={!valid}
                        isLoading={busy}
                        onClick={doPreview}
                      >
                        Previsualizar <Icon icon="ki-eye" size="fs-4 ms-2" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-light fs-7 fw-bold"
                        onClick={() => setPreview(null)}
                        disabled={busy}
                      >
                        <Icon icon="ki-left" size="fs-4 me-1" /> Corregir
                      </button>
                      <Button
                        type="button"
                        className="btn btn-danger fs-7 fw-bold"
                        isLoading={busy}
                        onClick={doApply}
                      >
                        Aplicar en OpenSIPS <Icon icon="ki-check" size="fs-4 ms-2" />
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}

            {result && (
              <>
                <ReloadBanner reload={result.reload} what="lb_reload" />
                <div className="d-flex justify-content-center pt-8">
                  <button
                    type="button"
                    className="btn btn-primary fs-7 fw-bold"
                    onClick={() => close(true)}
                  >
                    Cerrar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
